import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PermissionGate from '../../components/PermissionGate';
import useConfirm from '../../components/ConfirmDialog/useConfirm';
import { S, Icon, StatusBadge, BtnPrimary, BtnSecondary, DataCard, formatCHF, formatDate } from '../regureStyles';
import NewPolicyPanel from '../PoliciesScreen/NewPolicyPanel';
import RegistrationNumberField from './RegistrationNumberField';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Offer-scoped label overrides — see Table.js note. `sent` renders as
// "Issued" because nothing is actually sent on create; the state just
// means finalised/no longer editable.
const OFFER_STATUS_LABEL_KEYS = {
  draft:    'offers.list.statusDraft',
  sent:     'offers.list.statusSent',
  accepted: 'offers.list.statusAccepted',
  declined: 'offers.list.statusDeclined',
  expired:  'offers.list.statusExpired',
};


const thStyle = { textAlign: 'left', padding: '4px 6px', fontSize: 10, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${S.borderLight}` };
const tdStyle = { padding: '6px', fontSize: 12, color: S.textPrimary, borderBottom: `1px solid ${S.borderLight}` };

// Activity is stored as a 0-1 fraction and read back as the percentage the
// premium was prorated by. 0.3 -> "30%".
const fmtActivityPct = (fte) => {
  const n = Number(fte);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 10000) / 100}%`;
};

// WHO MAY DELETE THIS QUOTE.
//
// Mirrors offerController.canDeleteOffer exactly: a systemAdmin (or the
// platform admin), or the person whose account keyed the quote. The server
// is authoritative — this only decides whether to render a control that
// would otherwise 403 on click.
//
// `createdBy` is the historical fact of who keyed it and is never
// reassigned; `handler` deliberately does NOT grant deletion, because the
// gestionnaire is a workflow field anybody can be moved into.
const DELETE_ADMIN_ROLES = new Set(['admin', 'systemAdmin']);
const mayDeleteOffer = (user, offer) => {
  if (!user?.role || !offer?._id) return false;
  if (offer.convertedTo) return false;
  if (DELETE_ADMIN_ROLES.has(user.role)) return true;
  return Boolean(offer.createdBy) && String(offer.createdBy) === String(user._id);
};

const OfferDetailPanel = ({ offer, onClose, onUpdated, onRevise, onEditDraft, onOpenOffer }) => {
  const { t, i18n } = useTranslation();
  const { confirm, ConfirmPortal } = useConfirm();
  // Same two response shapes PermissionGate reads.
  const currentUser = useSelector((state) => {
    const info = state?.userLogin?.userInfo?.data;
    return info?.data || info?.user || null;
  });
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [showConvert, setShowConvert] = useState(false);
  // Revision lineage, read back from GET /offers/:id. The panel is opened
  // with a row from the list, which carries no lineage, so this is fetched
  // rather than passed. Additive: the panel renders fine without it.
  const [lineage, setLineage] = useState(offer.lineage || null);
  // The rated inputs, read back from the same request. The list row carries
  // only the total, so the per-person breakdown — the specialty and the
  // activity percentage each premium was prorated by — has to come from
  // the stored offer. Without it the panel could not show what was quoted,
  // only what it added up to.
  const [ratedDetail, setRatedDetail] = useState(null);
  useEffect(() => {
    if (!offer._id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BASE_URL}/offers/${offer._id}`, { withCredentials: true });
        if (cancelled) return;
        setLineage(r.data?.data?.lineage || null);
        setRatedDetail(r.data?.data || null);
      } catch { /* lineage is additive — never block the panel on it */ }
    })();
    return () => { cancelled = true; };
  }, [offer._id]);
  const [generatingDoc, setGeneratingDoc] = useState(false);
  // P1-C: which language the document actually came out in, and whether
  // that differed from the interface language. The backend decides —
  // carrier legal wording only exists in approved languages — and says
  // so on the response, so the user finds out here rather than on
  // page 3 of the PDF.
  const [docLocaleNotice, setDocLocaleNotice] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);
  // Optimistic local additions to the "Previously Generated" list, so a
  // freshly-generated document appears immediately without waiting for
  // the parent's re-fetch to finish. Deduped against offer.generatedDocuments
  // by _id at render time.
  const [localDocs, setLocalDocs] = useState([]);
  const [locallyDeletedIds, setLocallyDeletedIds] = useState([]);
  // D-18: draft offers were read-only in the detail panel — no way to
  // amend notes or the expiry date without deleting and re-creating.
  // Full recreation of the pricing form here would double the panel
  // and risk drift with NewOfferPanel; instead expose an inline
  // edit-mode for the fields that get amended most often (notes,
  // expiresAt). Status-changing edits go through the existing
  // Send/Accept/Decline buttons.
  const [editing, setEditing] = useState(false);
  const [editNotes, setEditNotes] = useState(offer.notes || '');
  const [editExpires, setEditExpires] = useState(
    offer.expiresAt ? String(offer.expiresAt).slice(0, 10) : (offer.expires || '')
  );
  const [savingEdit, setSavingEdit] = useState(false);

  const saveEdits = async () => {
    if (!offer._id) return;
    setSavingEdit(true);
    setError('');
    try {
      const resp = await axios.patch(
        `${BASE_URL}/offers/${offer._id}`,
        { notes: editNotes, expiresAt: editExpires || null },
        { withCredentials: true },
      );
      setEditing(false);
      // D-18: parent re-fetches the list but the panel keeps rendering
      // the stale `selectedOffer` reference until the user reopens it.
      // Hand the updated doc back so the parent can swap selectedOffer.
      const updated = resp?.data?.data || null;
      onUpdated && onUpdated(updated);
    } catch (err) {
      setError(err.response?.data?.message || t('offers.detail.updateFailed'));
    } finally {
      setSavingEdit(false);
    }
  };

  const updateStatus = async newStatus => {
    if (!offer._id) {
      // mock mode: just close
      onClose();
      return;
    }
    setUpdating(newStatus);
    setError('');
    try {
      await axios.patch(`${BASE_URL}/offers/${offer._id}`, { status: newStatus }, { withCredentials: true });
      onUpdated && onUpdated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('offers.detail.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  // Revise — reopens the quoting form pre-filled with every detail from
  // this offer so an already-issued quote can be amended without
  // re-keying anything. The source offer is deliberately left untouched:
  // it is a document that was actually sent to the client, and editing it
  // in place would destroy the record of what they were shown. Saving
  // produces a new offer stamped as the next variant, with the premium
  // recomputed from whatever the user changed.
  const startRevision = () => {
    if (!offer._id || !onRevise) return;
    onRevise(offer);
  };

  // Generate offer document (PDF) using the platform template for this
  // LOB, then immediately fetch a short-lived signed URL and download.
  // Two-step (generate → download) so the browser gets a clean file
  // save prompt from S3 rather than a blob URL from the backend.
  const generateDocument = async () => {
    if (!offer._id) return;
    setGeneratingDoc(true);
    setError('');
    setDocLocaleNotice(null);
    try {
      // D-07: send the current UI locale explicitly so the PDF renders
      // in the language the user is looking at right now, even if they
      // haven't persisted the change to their profile yet.
      const uiLocale = (i18n.language || 'fr').slice(0, 2);
      const genResp = await axios.post(`${BASE_URL}/offers/${offer._id}/generate-document`, { locale: uiLocale }, { withCredentials: true });
      const row = genResp?.data?.data;
      setDocLocaleNotice(
        genResp?.data?.localeRestricted
          ? {
            produced: genResp.data.locale,
            requested: genResp.data.localeRequested,
            // WHY it was restricted, not just that it was. A carrier
            // template is bounded by wording the insurer supplied; the
            // generic tenant-branded proposal has no insurer wording in it
            // at all and is bounded by what the platform draws. One
            // sentence cannot honestly explain both.
            template: genResp.data.template,
          }
          : null,
      );
      if (!row?._id) throw new Error(t('offers.detail.docGenFailed', { defaultValue: 'Document generation failed' }));
      const dlResp = await axios.get(`${BASE_URL}/offers/${offer._id}/documents/${row._id}/download`, { withCredentials: true });
      const url = dlResp?.data?.data?.url;
      if (url) window.open(url, '_blank');
      // Optimistic append so the "Previously Generated" list updates
      // without waiting for the parent's refetch round-trip.
      setLocalDocs(prev => [...prev, row]);
      onUpdated && onUpdated();
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('offers.detail.docGenFailed', { defaultValue: 'Document generation failed' }));
    } finally {
      setGeneratingDoc(false);
    }
  };

  const downloadExistingDoc = async (docId) => {
    if (!offer._id || !docId) return;
    try {
      const dlResp = await axios.get(`${BASE_URL}/offers/${offer._id}/documents/${docId}/download`, { withCredentials: true });
      const url = dlResp?.data?.data?.url;
      if (url) window.open(url, '_blank');
    } catch (err) {
      setError(err.response?.data?.message || t('offers.detail.docDownloadFailed', { defaultValue: 'Download failed' }));
    }
  };

  const deleteExistingDoc = async (docId, filename) => {
    if (!offer._id || !docId) return;
    const ok = await confirm({
      title: t('offers.detail.deleteDocTitle', { defaultValue: 'Delete generated document?' }),
      message: t('offers.detail.deleteDocMessage', {
        defaultValue: '"{{filename}}" will be permanently removed from this offer. An audit entry will be recorded.',
        filename,
      }),
      confirmText: t('common.delete', { defaultValue: 'Delete' }),
      variant: 'danger',
    });
    if (!ok) return;
    setDeletingDocId(docId);
    setError('');
    try {
      await axios.delete(`${BASE_URL}/offers/${offer._id}/documents/${docId}`, { withCredentials: true });
      setLocallyDeletedIds(prev => [...prev, docId]);
      onUpdated && onUpdated();
    } catch (err) {
      setError(err.response?.data?.message || t('offers.detail.docDeleteFailed', { defaultValue: 'Failed to delete document.' }));
    } finally {
      setDeletingDocId(null);
    }
  };

  // DELETE THIS QUOTE — a soft delete, and the wording says so.
  //
  // AROMED's ask (Samire, 2026-09-07) was to clear accidental and duplicate
  // quotes out of the list. What the server does is stamp `deletedAt`: the
  // record, its number and its generated documents all survive and it can be
  // restored in the database. The confirm text promises exactly that and no
  // more — a dialog that said "permanently removed" would be a lie, and one
  // that said nothing would leave the user assuming the worse of the two.
  const deleteOffer = async () => {
    if (!offer._id) return;
    const ok = await confirm({
      title: t('offers.detail.deleteTitle', { defaultValue: 'Delete this quote?' }),
      // {{offerNumber}}, not a JS template literal. i18next interpolates the
      // defaultValue too, so this stays one string in two places rather than
      // two strings that can drift — and the fallback then reads the same as
      // the bundle entry instead of quietly being a different sentence.
      message: t('offers.detail.deleteMessage', {
        defaultValue: 'Quote {{offerNumber}} will be removed from the offers list, its filters and its counts. It is not destroyed — the record is kept and can be restored, and an audit entry is written.',
        offerNumber: offer.offerNumber,
      }),
      confirmText: t('common.delete', { defaultValue: 'Delete' }),
      variant: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`${BASE_URL}/offers/${offer._id}`, { withCredentials: true });
      onUpdated && onUpdated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message
        || t('offers.detail.deleteFailed', { defaultValue: 'Failed to delete this quote.' }));
      setDeleting(false);
    }
  };

  // Line items only exist for RC Pro; every other product renders the card
  // away rather than showing an empty table.
  const ratedCalc = ratedDetail?.params?.calculation;
  const rcProLines = ratedCalc?.product === 'RC_PRO' && Array.isArray(ratedCalc.lineItems)
    ? ratedCalc.lineItems
    : [];

  // Motor readback. Blank fields are dropped rather than rendered as
  // labelled dashes: the quote captured what it captured, and a column of
  // em-dashes reads as missing data rather than as data never asked for.
  const motorParams = (ratedDetail?.params || offer.params)?.motor;
  const motorRows = motorParams
    ? [
      [t('offers.newOffer.motor.make'),          motorParams.vehicleMake],
      [t('offers.newOffer.motor.model'),         motorParams.vehicleModel],
      [t('offers.newOffer.motor.year'),          motorParams.yearOfManufacture],
      [t('offers.newOffer.motor.plate'),         motorParams.plateNumber],
      [t('offers.newOffer.motor.chassis'),       motorParams.chassisNumber],
      // The formatted value already carries the currency, so the readback
      // label drops it — "Sum Insured (BHD) … BHD 28,500" says it twice.
      [t('offers.newOffer.motor.vehicleValuePlain'),
        motorParams.vehicleValue == null || motorParams.vehicleValue === ''
          ? '' : formatCHF(Number(motorParams.vehicleValue), offer.currency)],
      [t('offers.newOffer.motor.coverageType'),  motorParams.coverageType],
      [t('offers.newOffer.motor.driverName'),    motorParams.driverName],
      [t('offers.newOffer.motor.dateOfBirth'),   motorParams.driverDateOfBirth ? formatDate(motorParams.driverDateOfBirth) : ''],
      [t('offers.newOffer.motor.licenseNumber'), motorParams.licenseNumber],
      [t('offers.newOffer.motor.ncd'),           motorParams.noClaimsDiscount],
    ].filter(([, v]) => v !== undefined && v !== null && v !== '')
    : [];

  const Row = ({ label, value, valueStyle }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${S.borderLight}` }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary, ...valueStyle }}>{value}</span>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.3)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        style={{ width: 440, height: '100%', background: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.15)', overflowY: 'auto', padding: 28 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{t('offers.detail.quoteDetails')}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: S.burgundy }}>{offer.id}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={20} />
          </button>
        </div>

        {/* Status badge */}
        <div style={{ marginBottom: 20 }}>
          <StatusBadge status={offer.status} label={OFFER_STATUS_LABEL_KEYS[offer.status] ? t(OFFER_STATUS_LABEL_KEYS[offer.status]) : undefined} />
        </div>

        {/* Detail rows */}
        <DataCard style={{ marginBottom: 20 }}>
          <Row label={t('offers.detail.client')} value={offer.client || '—'} />
          <Row label={t('offers.detail.product')} value={offer.product || '—'} />
          <Row label={t('offers.detail.premium')} value={formatCHF(offer.premiumCalc)} valueStyle={{ color: S.burgundy, fontSize: 16 }} />
          <Row label={t('offers.detail.broker')} value={offer.broker || '—'} />
          <Row label={t('offers.detail.created')} value={formatDate(offer.created)} />
          {editing ? (
            <div style={{ padding: '10px 0', borderBottom: `1px solid ${S.borderLight}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{t('offers.detail.expires')}</div>
              <input
                type='date'
                value={editExpires}
                onChange={e => setEditExpires(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: `1px solid ${S.borderLight}`, borderRadius: 6, fontSize: 13 }}
              />
            </div>
          ) : (
            <Row label={t('offers.detail.expires')} value={formatDate(offer.expires)} valueStyle={{ color: offer.status === 'expired' ? '#ef4444' : S.textPrimary }} />
          )}
        </DataCard>

        {/* Per-person rating readback. RC Pro prorates each practitioner's
            specialty premium by the activity percentage they actually work,
            so the saved activity is part of what was quoted and has to be
            visible here — not only inside the generated PDF. */}
        {rcProLines.length > 0 && (
          <DataCard style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
              {t('offers.detail.insuredPersons', { defaultValue: 'Insured persons' })}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>{t('offers.rcpro.personnelColumn')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('offers.rcpro.activityColumn')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('offers.rcpro.premiumColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {rcProLines.map((li, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{li.label}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtActivityPct(li.activityFTE)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCHF(li.linePremium)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataCard>
        )}

        {/* What was actually quoted. A motor proposal is judged on the risk
            details, not on the premium alone, so the captured vehicle and
            driver have to be readable here and not only inside the PDF —
            same reason the RC Pro per-person lines are shown above. */}
        {motorRows.length > 0 && (
          <DataCard style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
              {t('offers.detail.riskDetails', { defaultValue: 'Risk details' })}
            </div>
            {motorRows.map(([label, value]) => (
              <Row key={label} label={label} value={value} />
            ))}
          </DataCard>
        )}

        {/* The intermediary's registration number, printed on this offer's
            documents. Editable until the offer is converted; the update
            role gates it like the notes below. */}
        {(() => {
          // The list row may not carry the field (the list mapper builds its
          // own shape); the stored offer fetched above always does.
          const regOffer = {
            ...offer,
            intermediaryRegistrationNumber: offer.intermediaryRegistrationNumber
              ?? ratedDetail?.intermediaryRegistrationNumber ?? '',
          };
          return (
            <PermissionGate
              resource='offers' action='update'
              fallback={<RegistrationNumberField offer={regOffer} canEdit={false} />}
            >
              <RegistrationNumberField
                offer={regOffer}
                canEdit={!offer.convertedTo}
                onUpdated={(updated) => {
                  if (updated) setRatedDetail(prev => ({ ...(prev || {}), intermediaryRegistrationNumber: updated.intermediaryRegistrationNumber }));
                  onUpdated && onUpdated(updated);
                }}
              />
            </PermissionGate>
          );
        })()}

        {/* D-18: notes editor + Edit toggle. RBAC gate matches Send/Accept
            actions (offers.update). Draft-only per UX intent — after send
            an offer is frozen; wording changes need a new variant. */}
        <PermissionGate resource='offers' action='update'>
          {offer.status === 'draft' && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {t('offers.detail.notesHeader', { defaultValue: 'Notes' })}
                </div>
                {!editing ? (
                  <button type='button' onClick={() => setEditing(true)} style={{ fontSize: 12, color: S.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    <Icon name='edit' color={S.burgundy} size={12} /> {t('offers.detail.edit', { defaultValue: 'Edit' })}
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type='button' onClick={() => { setEditing(false); setEditNotes(offer.notes || ''); setEditExpires(offer.expiresAt ? String(offer.expiresAt).slice(0, 10) : (offer.expires || '')); }} style={{ fontSize: 12, color: S.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>
                      {t('offers.detail.cancel', { defaultValue: 'Cancel' })}
                    </button>
                    <button type='button' onClick={saveEdits} disabled={savingEdit} style={{ fontSize: 12, color: '#fff', background: S.burgundy, border: 'none', padding: '4px 10px', borderRadius: 4, cursor: savingEdit ? 'wait' : 'pointer', fontWeight: 600 }}>
                      {savingEdit ? t('offers.detail.saving', { defaultValue: 'Saving…' }) : t('offers.detail.save', { defaultValue: 'Save' })}
                    </button>
                  </div>
                )}
              </div>
              {editing ? (
                <textarea
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  rows={4}
                  style={{ width: '100%', padding: 10, border: `1px solid ${S.borderLight}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                />
              ) : (
                <div style={{ fontSize: 13, color: S.textSecondary, whiteSpace: 'pre-wrap', minHeight: 20 }}>
                  {offer.notes || t('offers.detail.noNotes', { defaultValue: '(no notes)' })}
                </div>
              )}
            </div>
          )}
        </PermissionGate>

        {/* Status actions — hidden for read-only roles (adjuster, underwriter,
            complianceOfficer, portal users). Gated on offers.update since
            Accept/Decline/Send all mutate offer status. */}
        <PermissionGate resource='offers' action='update'>
          {offer.status === 'draft' && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('offers.detail.actions')}</div>
              <BtnPrimary onClick={() => updateStatus('sent')} disabled={!!updating} style={{ width: '100%', justifyContent: 'center' }}>
                <Icon name='send' color='#fff' size={14} /> {updating === 'sent' ? t('offers.detail.updating') : t('offers.detail.sendToClient')}
              </BtnPrimary>
            </div>
          )}

          {offer.status === 'sent' && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('offers.detail.actions')}</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <BtnPrimary
                  onClick={() => updateStatus('accepted')}
                  disabled={!!updating}
                  style={{ flex: 1, justifyContent: 'center', background: '#10b981', borderColor: '#10b981' }}
                >
                  <Icon name='check' color='#fff' size={14} /> {updating === 'accepted' ? t('offers.detail.updating') : t('offers.detail.accept')}
                </BtnPrimary>
                <BtnSecondary
                  onClick={() => updateStatus('declined')}
                  disabled={!!updating}
                  style={{ flex: 1, justifyContent: 'center', color: '#ef4444', borderColor: '#fca5a5' }}
                >
                  <Icon name='x' color='#ef4444' size={14} /> {updating === 'declined' ? t('offers.detail.updating') : t('offers.detail.decline')}
                </BtnSecondary>
              </div>
            </div>
          )}
        </PermissionGate>

        {/* Version history. `revisionOf` / `variant` have been stored since
            Revise shipped and were never shown, so a revision and the quote
            it replaced read as two unrelated offers. Both directions are
            listed: which quote this one replaced, and what has superseded
            it — the second is what stops the wrong version going out. */}
        {lineage && (lineage.variant > 1 || lineage.previous || (lineage.revisions || []).length > 0) && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              {t('offers.detail.versionHeader')}
            </div>
            <div style={{ fontSize: 12, color: S.textPrimary, marginBottom: 6 }}>
              {t('offers.detail.versionCurrent', { n: lineage.variant })}
              {lineage.isCurrent
                ? ` · ${t('offers.detail.versionIsCurrent')}`
                : ` · ${t('offers.detail.versionSuperseded')}`}
            </div>
            {/* THE WHOLE CHAIN, not one hop in each direction.
                The offers list now shows one row per proposal, so this
                panel is the only route to the other versions. On a
                v1→v2→v3 chain the one-hop lists left v1 unreachable from
                the row the user can actually see. */}
            {(lineage.chain || []).length > 1 && (lineage.chain || []).map(v => {
              const isThis = String(v._id) === String(offer._id);
              return (
                <button
                  key={v._id}
                  type='button'
                  disabled={isThis}
                  onClick={() => !isThis && onOpenOffer && onOpenOffer(v._id)}
                  style={{ display: 'block', width: '100%', background: isThis ? S.bgHover : 'none', border: 'none', padding: '5px 8px', marginBottom: 3, borderRadius: 4, fontSize: 12, fontWeight: isThis ? 700 : 400, color: isThis ? S.textPrimary : (S.accent || S.textSecondary), cursor: isThis ? 'default' : (onOpenOffer ? 'pointer' : 'default'), textAlign: 'left' }}
                >
                  {t('offers.detail.versionRow', {
                    n: v.variant || 1,
                    ref: v.offerNumber,
                    status: v.status,
                    defaultValue: 'v{{n}} · {{ref}} · {{status}}',
                  })}
                  {isThis ? ` — ${t('offers.detail.versionThis', { defaultValue: 'viewing' })}` : ''}
                </button>
              );
            })}
            {(lineage.chain || []).length <= 1 && lineage.previous && (
              <button
                type='button'
                onClick={() => onOpenOffer && onOpenOffer(lineage.previous._id)}
                style={{ display: 'block', background: 'none', border: 'none', padding: 0, marginBottom: 4, fontSize: 12, color: S.accent || S.textSecondary, cursor: onOpenOffer ? 'pointer' : 'default', textAlign: 'left' }}
              >
                {t('offers.detail.versionReplaces', { ref: lineage.previous.offerNumber })}
              </button>
            )}
            {(lineage.chain || []).length <= 1 && (lineage.revisions || []).map(r => (
              <button
                key={r._id}
                type='button'
                onClick={() => onOpenOffer && onOpenOffer(r._id)}
                style={{ display: 'block', background: 'none', border: 'none', padding: 0, marginBottom: 4, fontSize: 12, color: S.accent || S.textSecondary, cursor: onOpenOffer ? 'pointer' : 'default', textAlign: 'left' }}
              >
                {t('offers.detail.versionReplacedBy', { ref: r.offerNumber, n: r.variant || 2 })}
              </button>
            ))}
          </div>
        )}

        {/* EDIT A DRAFT IN PLACE.
            A draft used to be the LEAST editable state in the system: the
            notes/expiry edit above was all of it, and Revise was hidden
            precisely here, so a wrong figure on a saved draft could only be
            fixed by deleting it and re-keying the whole quote. AROMED,
            tracker R32 (04.09.2026): "proposition sous brouillon, ne
            peuvent pas être reprise et modifiée".
            Reopens the quote form seeded from this draft; saving updates
            the same record, so no second draft appears. */}
        <PermissionGate resource='offers' action='update'>
          {onEditDraft && offer.status === 'draft' && !offer.convertedTo && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                {t('offers.detail.editDraftHeader', { defaultValue: 'Edit draft' })}
              </div>
              <BtnSecondary
                onClick={() => onEditDraft(offer)}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <Icon name='edit' color={S.textSecondary} size={14} />{' '}
                {t('offers.detail.editDraft', { defaultValue: 'Edit Quote' })}
              </BtnSecondary>
              <div style={{ fontSize: 11, color: S.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                {t('offers.detail.editDraftHelp', { defaultValue: 'Reopens the quote form with everything you entered. Change what you need and save — this stays the same draft, with the same number.' })}
              </div>
            </div>
          )}
        </PermissionGate>

        {/* Revise — how an ISSUED proposal is changed. It stays the same
            proposal: the number is kept and the change is recorded as the
            next version, because the previous version is a document the
            client was actually shown and rewriting it in place would
            destroy that record. Gated on offers.create because it writes a
            new version. Hidden once converted: the policy is the live
            record from that point and the offer can no longer move. */}
        <PermissionGate resource='offers' action='create'>
          {onRevise && offer.status !== 'draft' && !offer.convertedTo && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                {t('offers.detail.reviseHeader', { defaultValue: 'Revise' })}
              </div>
              <BtnSecondary
                onClick={startRevision}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <Icon name='edit' color={S.textSecondary} size={14} />{' '}
                {t('offers.detail.revise', { defaultValue: 'Revise Offer' })}
              </BtnSecondary>
              <div style={{ fontSize: 11, color: S.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                {t('offers.detail.reviseHelp', { defaultValue: 'Reopens the quote form pre-filled with every detail from this proposal. Adjust what changed and save. It keeps the same proposal number and stays one entry in the list — the version you are looking at is preserved as history.' })}
              </div>
            </div>
          )}
        </PermissionGate>

        {/* Convert to Policy — visible on accepted offers that haven't been
            converted yet. RBAC-gated on policies.create so read-only roles
            (adjuster, complianceOfficer) don't see it. */}
        <PermissionGate resource='policies' action='create'>
          {offer.status === 'accepted' && !offer.convertedTo && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                {t('offers.detail.convertHeader', { defaultValue: 'Bind to Policy' })}
              </div>
              <BtnPrimary
                onClick={() => setShowConvert(true)}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <Icon name='file-check' color='#fff' size={14} /> {t('offers.detail.convertToPolicy', { defaultValue: 'Convert to Policy' })}
              </BtnPrimary>
              <div style={{ fontSize: 11, color: S.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                {t('offers.detail.convertHelp', { defaultValue: 'Opens the policy form pre-filled from this offer. Broker, premium, product, and coverage carry over. You set the effective dates and confirm.' })}
              </div>
            </div>
          )}
          {offer.convertedTo && (
            <div style={{ marginBottom: 12, padding: '10px 14px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 12, color: '#065f46' }}>
              {t('offers.detail.alreadyConverted', { defaultValue: 'This offer has been converted to a policy.' })}
            </div>
          )}
        </PermissionGate>

        {/* Offer document generation. RBAC-gated on offers.update since the
            generation is a persisted mutation on the offer (adds a row to
            generatedDocuments[]) + writes to S3.

            This used to be gated on a hardcoded {RC_PRO, PGMA, PGM} set, so
            a tenant selling Motor or Property could quote and never produce
            a document — the backend would have built one, the button was
            simply never rendered. offerDocumentService.templateForLob has
            answered for EVERY line of business since the generic
            tenant-branded proposal landed, so the only question left is
            whether this offer has a product at all. */}
        {Boolean(offer.lineOfBusiness) && (
          <PermissionGate resource='offers' action='update'>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                {t('offers.detail.documentsHeader', { defaultValue: 'Offer Document' })}
              </div>
              <BtnSecondary
                onClick={generateDocument}
                disabled={generatingDoc}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <Icon name='file-text' color={S.textPrimary} size={14} />
                {generatingDoc
                  ? t('offers.detail.docGenGenerating', { defaultValue: 'Generating…' })
                  : t('offers.detail.docGenerate',       { defaultValue: 'Generate Offer Document' })}
              </BtnSecondary>
              {docLocaleNotice && (
                <div
                  role='status'
                  style={{
                    marginTop: 10, padding: '9px 12px', background: '#fffbeb',
                    border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e',
                  }}
                >
                  {t(
                    docLocaleNotice.template === 'generic'
                      ? 'offers.detail.docLocaleRestrictedGeneric'
                      : 'offers.detail.docLocaleRestricted',
                    {
                      produced: t(`common.languages.${docLocaleNotice.produced}`, { defaultValue: docLocaleNotice.produced.toUpperCase() }),
                      requested: t(`common.languages.${docLocaleNotice.requested}`, { defaultValue: docLocaleNotice.requested.toUpperCase() }),
                    },
                  )}
                </div>
              )}
              {(() => {
                const serverDocs = offer.generatedDocuments || [];
                const seen = new Set(serverDocs.map(d => String(d._id)));
                const merged = [
                  ...serverDocs,
                  ...localDocs.filter(d => !seen.has(String(d._id))),
                ].filter(d => !locallyDeletedIds.includes(String(d._id)));
                if (merged.length === 0) return null;
                return (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 2 }}>
                    {t('offers.detail.docGenPrev', { defaultValue: '{{count}} previously generated', count: merged.length })}
                  </div>
                  {[...merged]
                    .sort((a, b) => new Date(b.generatedAt || 0) - new Date(a.generatedAt || 0))
                    .map(d => (
                    <div key={d._id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px',
                        background: '#fff',
                        border: `1px solid ${S.borderLight}`,
                        borderRadius: 8,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: S.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.filename}
                        </div>
                        <div style={{ fontSize: 11, color: S.textMuted, marginTop: 1 }}>
                          {d.generatedAt ? formatDate(d.generatedAt) : '—'} · {d.template || '—'}
                        </div>
                      </div>
                      <button
                        type='button'
                        onClick={() => downloadExistingDoc(d._id)}
                        title={t('offers.detail.docDownload', { defaultValue: 'Download' })}
                        style={{ padding: 5, background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke={S.textMuted} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                          <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                          <polyline points='7 10 12 15 17 10' />
                          <line x1='12' y1='15' x2='12' y2='3' />
                        </svg>
                      </button>
                      <PermissionGate resource='offers' action='delete'>
                        <button
                          type='button'
                          onClick={() => deleteExistingDoc(d._id, d.filename)}
                          disabled={deletingDocId === d._id}
                          title={t('common.delete', { defaultValue: 'Delete' })}
                          style={{
                            padding: 5, background: 'none', border: 'none',
                            cursor: deletingDocId === d._id ? 'wait' : 'pointer',
                            opacity: deletingDocId === d._id ? 0.5 : 1,
                          }}
                        >
                          <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#dc2626' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                            <polyline points='3 6 5 6 21 6' />
                            <path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' />
                          </svg>
                        </button>
                      </PermissionGate>
                    </div>
                  ))}
                </div>
                );
              })()}
            </div>
          </PermissionGate>
        )}

        {error && (
          <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 14 }}>
            {error}
          </div>
        )}

        {/* Delete quote — RBAC opens the door, mayDeleteOffer decides. A
            converted offer is never offered the control: the policy it
            produced would be left pointing at a quote the list denies. */}
        <PermissionGate resource='offers' action='delete'>
          {mayDeleteOffer(currentUser, offer) && (
            <div style={{ marginBottom: 12, paddingTop: 12, borderTop: `1px solid ${S.borderLight}` }}>
              <BtnSecondary
                onClick={deleteOffer}
                disabled={deleting}
                style={{ width: '100%', justifyContent: 'center', color: '#991b1b', borderColor: '#fca5a5' }}
              >
                <Icon name='trash' color='#991b1b' size={14} />{' '}
                {deleting
                  ? t('offers.detail.deleting', { defaultValue: 'Deleting…' })
                  : t('offers.detail.delete', { defaultValue: 'Delete Quote' })}
              </BtnSecondary>
              <div style={{ fontSize: 11, color: S.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                {t('offers.detail.deleteHelp', { defaultValue: 'Removes this quote from the offers list, its filters and its counts. The record is kept and can be restored; an audit entry is written.' })}
              </div>
            </div>
          )}
        </PermissionGate>

        <BtnSecondary onClick={onClose} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
          {t('offers.detail.close')}
        </BtnSecondary>
      </div>

      {showConvert && (
        <NewPolicyPanel
          sourceOffer={offer}
          onClose={() => setShowConvert(false)}
          onCreated={() => {
            setShowConvert(false);
            onUpdated && onUpdated();
            onClose();
          }}
        />
      )}
      {ConfirmPortal}
    </div>
  );
};

export default OfferDetailPanel;
